import React, { useState, useEffect, useRef, useCallback } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import { loadMoreListings } from './ProfilePage.duck';
import { IconSpinner } from '../../components';
import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {isCurrentUser ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const [selectedCategory, setSelectedCategory] = useState('mostRecent');
  const [isProviderExpanded, setIsProviderExpanded] = useState(false);
  const [isCustomerExpanded, setIsCustomerExpanded] = useState(false);

  const { reviews, queryReviewsError } = props;

  // Separate reviews based on type
  let reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const uuidsToRemove = [
    '673cde45-ccb3-49fa-823b-c6a358775c29',
    '673cf9e2-df88-437a-894d-088da91a777c',
    '66f09382-9b55-4dc8-9e46-53f1a36667d9',
    '67339317-e167-46a0-8195-1485932cb508',
    '69b4666f-c95f-4802-9974-ce4a605a1388',
    '6a3d9bbc-6e63-42cb-bdff-3a6603e0c973',
    '6a627a7b-c5ac-40c1-9794-486cd51ced6e',
    '6a690e85-ac76-440d-8009-8c0785fb9e67',
    '6a9ab1dd-1090-47d1-ad86-c53323f2367b',
    '6a9b341c-87e5-4999-bfb9-7280b18beec3',
    '6aa3733a-1a8c-403e-ba39-67a66e6766be',
    '6aa3293e-78a8-403e-adc9-2867fdf4f33d',
    '6aa27afb-dec9-44bb-838c-4c1c4247913c',
    '6aa84d67-93cd-451b-bace-7835f89969a2',
  ];
  reviewsOfProvider = reviewsOfProvider.filter(review => !uuidsToRemove.includes(review.id.uuid));
  const currentDate = '2025-04-08T04:48:39.553Z'; // or pass in a custom date
  const targetUuid = '67a69c23-6119-4f73-ad5b-7de6a1b0da81';

  reviewsOfProvider = reviewsOfProvider.map(review => {
    if (review.id.uuid === targetUuid) {
      return {
        ...review,
        attributes: {
          ...review.attributes,
          createdAt: currentDate,
        },
      };
    }
    return review;
  });

  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  // Sort function
  const sortReviews = (reviews, category) => {
    return [...reviews].sort((a, b) => {
      if (category === 'mostRecent') {
        return new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt);
      }
      if (category === 'highestRated') {
        return b.attributes.rating - a.attributes.rating;
      }
      if (category === 'lowestRated') {
        return a.attributes.rating - b.attributes.rating;
      }
      return 0;
    });
  };

  // Sorted reviews
  const sortedProviderReviews = sortReviews(reviewsOfProvider, selectedCategory);
  const sortedCustomerReviews = sortReviews(reviewsOfCustomer, selectedCategory);

  // Determine reviews to show
  const providerReviewsToShow = isProviderExpanded
    ? sortedProviderReviews
    : sortedProviderReviews.slice(0, 1);
  const customerReviewsToShow = isCustomerExpanded
    ? sortedCustomerReviews
    : sortedCustomerReviews.slice(0, 1);

  const lengthOfReviewsProvider = reviewsOfProvider.length;

  return (
    <div className={css.mobileReviews}>
      <div id="cometotop"></div>
      {/* Provider Reviews */}
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      {isProviderExpanded && (
        <div className={css.dropdownContainer}>
          <label htmlFor="providerReviewDropdown" className={css.dropdownLabel}>
            <FormattedMessage id="ProfilePage.sortReviews" defaultMessage="Sort by:" />
          </label>
          <select
            id="providerReviewDropdown"
            className={css.dropdown}
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="mostRecent">Most Recent</option>
            <option value="highestRated">Highest Rated</option>
            <option value="lowestRated">Lowest Rated</option>
          </select>
        </div>
      )}

      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={providerReviewsToShow} />
      {reviewsOfProvider.length > 1 && (
        <button
          className={css.showMoreButton}
          onClick={() => {
            if (isProviderExpanded) {
              const target = document.getElementById('cometotop');
              const yOffset = target.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top: yOffset, behavior: 'smooth' });
            }
            setIsProviderExpanded(prev => !prev);
          }}
        >
          {isProviderExpanded ? (
            <>
              <FormattedMessage id="ProfilePage.hideReviews" defaultMessage="Hide Reviews" />
              <span className={css.hidereviews}> ▲</span>
            </>
          ) : (
            <>
              <FormattedMessage
                id="ProfilePage.showReviews"
                defaultMessage={`More Reviews (${reviewsOfProvider.length})`}
                values={{ lengthOfReviewsProvider }}
              />
              <span className={css.showreviews}> ▼</span>
            </>
          )}
        </button>
      )}

      {/* Customer Reviews */}
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      {isCustomerExpanded && (
        <div className={css.dropdownContainer}>
          <label htmlFor="customerReviewDropdown" className={css.dropdownLabel}>
            <FormattedMessage id="ProfilePage.sortReviews" defaultMessage="Sort by:" />
          </label>
          <select
            id="customerReviewDropdown"
            className={css.dropdown}
            value={selectedCategory}
            onChange={e => setSelectedCategory(e.target.value)}
          >
            <option value="mostRecent">Most Recent</option>
            <option value="highestRated">Highest Rated</option>
            <option value="lowestRated">Lowest Rated</option>
          </select>
        </div>
      )}

      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={customerReviewsToShow} />
      {reviewsOfCustomer.length > 1 && (
        <button
          className={css.showMoreButton}
          onClick={() => {
            if (isCustomerExpanded) {
              const target = document.getElementById('cometotop');
              const yOffset = target.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top: yOffset, behavior: 'smooth' });
            }
            setIsCustomerExpanded(prev => !prev);
          }}
        >
          {isCustomerExpanded ? (
            <>
              <FormattedMessage id="ProfilePage.hideReviews" defaultMessage="Hide Reviews" />
              <span className={css.hidereviews}> ▲</span>
            </>
          ) : (
            <>
              <FormattedMessage
                id="ProfilePage.showReviews"
                defaultMessage={`More Reviews (${lengthOfReviewsProvider})`}
                values={{ lengthOfReviewsProvider }}
              />
              <span className={css.showreviews}> ▼</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

// bhushan code starts

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const [selectedCategory, setSelectedCategory] = useState('mostRecent');
  const [isExpanded, setIsExpanded] = useState(false);

  const { reviews, queryReviewsError } = props;

  // Separate reviews based on type
  let reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const uuidsToRemove = [
    '673cde45-ccb3-49fa-823b-c6a358775c29',
    '673cf9e2-df88-437a-894d-088da91a777c',
    '66f09382-9b55-4dc8-9e46-53f1a36667d9',
    '67339317-e167-46a0-8195-1485932cb508',
    '69b4666f-c95f-4802-9974-ce4a605a1388',
    '6a3d9bbc-6e63-42cb-bdff-3a6603e0c973',
    '6a627a7b-c5ac-40c1-9794-486cd51ced6e',
    '6a690e85-ac76-440d-8009-8c0785fb9e67',
    '6a9ab1dd-1090-47d1-ad86-c53323f2367b',
    '6a9b341c-87e5-4999-bfb9-7280b18beec3',
    '6aa3733a-1a8c-403e-ba39-67a66e6766be',
    '6aa3293e-78a8-403e-adc9-2867fdf4f33d',
    '6aa27afb-dec9-44bb-838c-4c1c4247913c',
    '6aa84d67-93cd-451b-bace-7835f89969a2',
  ];
  reviewsOfProvider = reviewsOfProvider.filter(review => !uuidsToRemove.includes(review.id.uuid));
  const currentDate = '2025-04-08T04:48:39.553Z'; // or pass in a custom date
  const targetUuid = '67a69c23-6119-4f73-ad5b-7de6a1b0da81';

  reviewsOfProvider = reviewsOfProvider.map(review => {
    if (review.id.uuid === targetUuid) {
      return {
        ...review,
        attributes: {
          ...review.attributes,
          createdAt: currentDate,
        },
      };
    }
    return review;
  });
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);

  // Determine currently selected reviews
  const currentReviews =
    showReviewsType === REVIEW_TYPE_OF_PROVIDER ? reviewsOfProvider : reviewsOfCustomer;

  // Sort reviews based on selected category
  const sortedReviews = [...currentReviews].sort((a, b) => {
    if (selectedCategory === 'mostRecent') {
      return new Date(b.attributes.createdAt) - new Date(a.attributes.createdAt);
    }
    if (selectedCategory === 'highestRated') {
      return b.attributes.rating - a.attributes.rating;
    }
    if (selectedCategory === 'lowestRated') {
      return a.attributes.rating - b.attributes.rating;
    }
    return 0;
  });

  // Determine reviews to show based on expanded state
  const reviewsToShow = isExpanded ? sortedReviews : sortedReviews.slice(0, 1);
  const lengthOfReviewsProvider = reviewsOfProvider.length;

  // Configure tabs
  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: showReviewsType === REVIEW_TYPE_OF_PROVIDER,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: showReviewsType === REVIEW_TYPE_OF_CUSTOMER,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <div id="cometotop"></div>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        {isExpanded && (
          <div className={css.dropdownContainer}>
            <label htmlFor="reviewDropdown" className={css.dropdownLabel}>
              <FormattedMessage id="ProfilePage.sortReviews" defaultMessage="Sort by:" />
            </label>
            <select
              id="reviewDropdown"
              className={css.dropdown}
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
            >
              <option value="mostRecent">Most Recent</option>
              <option value="highestRated">Highest Rated</option>
              <option value="lowestRated">Lowest Rated</option>
            </select>
          </div>
        )}

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        <Reviews reviews={reviewsToShow} />

        {currentReviews.length > 1 && (
          <button
            className={css.showMoreButton}
            onClick={() => {
              if (isExpanded) {
                const target = document.getElementById('cometotop');
                const yOffset = target.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({ top: yOffset, behavior: 'smooth' });
              }
              setIsExpanded(prev => !prev);
            }}
          >
            {isExpanded ? (
              <>
                <FormattedMessage id="ProfilePage.hideReviews" defaultMessage="Hide Reviews" />
                <span className={css.hidereviews}> ▲</span>
              </>
            ) : (
              <>
                <FormattedMessage
                  id="ProfilePage.showReviews"
                  defaultMessage={`More Reviews (${reviewsOfProvider.length})`}
                  values={{ lengthOfReviewsProvider }}
                />
                <span className={css.showreviews}> ▼</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};
// bhushan code ends

export const MainContent = props => {
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    reviews,
    queryReviewsError,
    viewport,
    listingsPagination,
    loadMoreListingsInProgress,
    userId,
  } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();
  const loadMoreRef = useRef(null);

  const hasListings = listings.length > 0;
  const isMobileLayout = viewport.width < MAX_MOBILE_SCREEN_WIDTH;
  const hasBio = !!bio;
  const hasMorePages = listingsPagination.currentPage < listingsPagination.totalPages;

  // Infinite scroll: observe sentinel element at bottom of listings
  useEffect(() => {
    if (!loadMoreRef.current || !hasMorePages || loadMoreListingsInProgress) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMorePages && !loadMoreListingsInProgress && userId) {
          dispatch(loadMoreListings(userId, config));
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [hasMorePages, loadMoreListingsInProgress, userId, config, dispatch]);

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H2>
      {hasBio ? <p className={css.bio}>{bio}</p> : null}
      {isMobileLayout ? (
        <MobileReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      ) : (
        <DesktopReviews reviews={reviews} queryReviewsError={queryReviewsError} />
      )}
      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage
              id="ProfilePage.listingsTitle"
              values={{ count: listingsPagination.totalItems || listings.length }}
            />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul>
          {hasMorePages ? (
            <div ref={loadMoreRef} className={css.loadMoreContainer}>
              {loadMoreListingsInProgress ? <IconSpinner /> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const ProfilePageComponent = props => {
  const config = useConfiguration();
  const { scrollingDisabled, currentUser, userShowError, user, intl, ...rest } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName } = profileUser?.attributes?.profile || {};

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage />;
  }
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<TopbarContainer currentPage="ProfilePage" />}
        sideNav={
          <AsideContent user={user} isCurrentUser={isCurrentUser} displayName={displayName} />
        }
        footer={<FooterContainer />}
      >
        <MainContent bio={bio} displayName={displayName} userShowError={userShowError} {...rest} />
      </LayoutSideNavigation>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
    listingsPagination,
    loadMoreListingsInProgress,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
    listingsPagination,
    loadMoreListingsInProgress,
    userId,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
